import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { DeliveryZoneArea, NeighborhoodWithPolygon } from '@/types/delivery-zone.types';
import { Loader2 } from 'lucide-react';

interface ZoneAreasMapProps {
  allNeighborhoods: NeighborhoodWithPolygon[];
  zoneAreas: DeliveryZoneArea[];
  selectedNeighborhoodId?: string;
  onNeighborhoodSelect?: (neighborhoodId: string) => void;
  onToggleNeighborhood?: (neighborhoodId: string) => void;
  togglingNeighborhoodId?: string | null;
  isLoading?: boolean;
}

export function ZoneAreasMap({ 
  allNeighborhoods, 
  zoneAreas,
  selectedNeighborhoodId, 
  onNeighborhoodSelect,
  onToggleNeighborhood,
  togglingNeighborhoodId,
  isLoading
}: ZoneAreasMapProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const layersRef = useRef<Map<string, L.Polygon>>(new Map());

  // Create lookup set for neighborhoods in zone
  const neighborhoodIdsInZone = new Set(zoneAreas.map(area => area.neighborhood_id));

  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    // Initialize map centered on Riyadh
    map.current = L.map(mapContainer.current).setView([24.72, 46.68], 11);

    // Add OpenStreetMap tiles
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
    }).addTo(map.current);

    return () => {
      map.current?.remove();
      map.current = null;
    };
  }, []);

  useEffect(() => {
    if (!map.current) return;

    // Clear existing layers
    layersRef.current.forEach((layer) => {
      map.current?.removeLayer(layer);
    });
    layersRef.current.clear();

    // Add polygons for each neighborhood
    const bounds: L.LatLngBounds | null = allNeighborhoods.length > 0 ? L.latLngBounds([]) : null;

    allNeighborhoods.forEach((neighborhood) => {
      // Check if neighborhood has polygon coordinates
      const coordinates = neighborhood.polygon?.coordinates?.[0];
      if (!coordinates || !Array.isArray(coordinates)) return;

      // Convert GeoJSON coordinates [lng, lat] to Leaflet [lat, lng]
      const latLngs: L.LatLngExpression[] = coordinates.map((coord: number[]) => [coord[1], coord[0]]);

      // Define colors based on whether neighborhood is in zone
      const inZone = neighborhoodIdsInZone.has(neighborhood.id);
      const isSelected = selectedNeighborhoodId === neighborhood.id;
      const isToggling = togglingNeighborhoodId === neighborhood.id;
      
      // Green for in-zone, Gray for available
      const fillColor = inZone ? '#22c55e' : '#94a3b8';  // green-500 : slate-400
      const strokeColor = inZone ? '#16a34a' : '#64748b'; // green-600 : slate-500

      const polygon = L.polygon(latLngs, {
        fillColor,
        fillOpacity: isSelected ? 0.6 : 0.35,
        color: strokeColor,
        weight: isSelected ? 3 : 2,
        className: isToggling ? 'animate-pulse' : '',
      }).addTo(map.current!);

      // Add popup with neighborhood name and toggle action
      const popupContent = `
        <div style="text-align: center; direction: rtl; min-width: 120px;">
          <strong>${neighborhood.name_ar || neighborhood.name}</strong>
          <br/>
          <span style="color: ${inZone ? '#22c55e' : '#64748b'}; font-size: 12px;">
            ${inZone ? 'في المنطقة ✓' : 'غير مضاف'}
          </span>
          <br/>
          <button 
            onclick="window.toggleNeighborhood && window.toggleNeighborhood('${neighborhood.id}')"
            style="
              margin-top: 8px;
              padding: 4px 12px;
              border-radius: 4px;
              border: none;
              cursor: pointer;
              font-size: 12px;
              background-color: ${inZone ? '#ef4444' : '#22c55e'};
              color: white;
            "
          >
            ${inZone ? 'إزالة' : 'إضافة'}
          </button>
        </div>
      `;
      polygon.bindPopup(popupContent);

      // Add click handler for selection
      polygon.on('click', () => {
        onNeighborhoodSelect?.(neighborhood.id);
      });

      // Double click to toggle
      polygon.on('dblclick', (e) => {
        L.DomEvent.stopPropagation(e);
        onToggleNeighborhood?.(neighborhood.id);
      });

      layersRef.current.set(neighborhood.id, polygon);

      // Extend bounds
      if (bounds) {
        bounds.extend(polygon.getBounds());
      }
    });

    // Fit map to bounds if there are neighborhoods with coordinates
    if (bounds && bounds.isValid()) {
      map.current.fitBounds(bounds, { padding: [30, 30] });
    }
  }, [allNeighborhoods, zoneAreas, selectedNeighborhoodId, togglingNeighborhoodId, neighborhoodIdsInZone, onNeighborhoodSelect, onToggleNeighborhood]);

  // Expose toggle function to window for popup button
  useEffect(() => {
    (window as any).toggleNeighborhood = (neighborhoodId: string) => {
      onToggleNeighborhood?.(neighborhoodId);
    };
    return () => {
      delete (window as any).toggleNeighborhood;
    };
  }, [onToggleNeighborhood]);

  return (
    <div className="relative w-full h-full min-h-[400px] rounded-lg overflow-hidden border z-0">
      <div ref={mapContainer} className="absolute inset-0 z-0" style={{ zIndex: 0 }} />
      
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/50 z-[1000]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {/* Legend */}
      <div className="absolute bottom-4 end-4 bg-background/90 backdrop-blur-sm rounded-lg p-3 shadow-lg border z-[1000]">
        <p className="text-xs font-medium mb-2">دليل الألوان</p>
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-500/60 border border-green-600" />
            <span className="text-xs">في المنطقة</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-slate-400/60 border border-slate-500" />
            <span className="text-xs">متاح للإضافة</span>
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground mt-2">انقر مرتين للإضافة/الإزالة</p>
      </div>

      {/* Empty state */}
      {allNeighborhoods.length === 0 && !isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted/50 z-[1000]">
          <p className="text-muted-foreground text-sm">لا توجد أحياء متاحة لهذه المدينة</p>
        </div>
      )}
    </div>
  );
}
